import {yupResolver} from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Card,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Box,
  LinearProgress,
  CircularProgress,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import axios from 'axios';
import DateTimePickerForm from 'components/DateTimePicker';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import {ENDPOINT} from 'constants';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import moment from 'moment';
import {Loading, Notify, Confirm} from 'notiflix';
import {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import CountryDropdown from 'components/country/CountryDropdown';
import {useCountry} from 'context/CountryContext';
import * as yup from 'yup';

const promoSchema = yup.object().shape({
  code: yup
    .string()
    .required('Promo code is required')
    .matches(/^[A-Z0-9]+$/, 'Only uppercase letters and numbers allowed')
    .min(4, 'Minimum 4 characters')
    .max(10, 'Maximum 10 characters'),

  description: yup.string().max(250, 'Maximum 250 characters'),

  discountValue: yup
    .number()
    .typeError('Must be a number')
    .required('Discount value is required')
    .min(1, 'Minimum value is 1')
    .test('max-discount', 'Percentage cannot exceed 100%', function (value) {
      const discountType = this.parent.discountType;
      if (discountType === 'percentage' && value > 100) return false;
      if (discountType === 'flat' && value > 10000) return false;
      return true;
    }),

  discountType: yup
    .string()
    .oneOf(['percentage', 'flat'], 'Invalid discount type')
    .required('Discount type is required'),

  maximumDiscountAmount: yup
    .number()
    .typeError('Must be a number')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Cannot be negative')
    .test('required-for-percentage', 'Required for percentage discount', function (value) {
      return (
        this.parent.discountType !== 'percentage' ||
        (value !== null && value !== undefined && value >= 0)
      );
    }),

  minimumCartAmount: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Cannot be negative')
    .default(0),

  expirationDate: yup
    .date()
    .typeError('Invalid date')
    .required('Expiration date is required')
    .min(moment().add(1, 'hour').toDate(), 'Expiration date must be at least 1 hour from now'),

  maximumNumberOfUsage: yup
    .number()
    .typeError('Must be a number')
    .required('Total usage limit is required')
    .integer('Must be a whole number')
    .min(1, 'Minimum 1 usage'),

  allowedUsagesPerUser: yup
    .number()
    .typeError('Must be a number')
    .required('Per user limit is required')
    .integer('Must be a whole number')
    .min(1, 'Minimum 1')
    .max(5, 'Maximum 5 per user'),

  promoScope: yup
    .string()
    .oneOf(['global', 'customer-specific'], 'Invalid scope')
    .required('Promo scope is required'),

  countryScope: yup
    .string()
    .oneOf(['all', 'specific'], 'Invalid country scope')
    .default('all'),

  allowedCountryId: yup.string().when('countryScope', {
    is: 'specific',
    then: schema => schema.required('Country is required'),
    otherwise: schema => schema.nullable(),
  }),

  isVisibleOnCart: yup.boolean().default(true),

  allowedUserIds: yup.array().when('promoScope', {
    is: 'customer-specific',
    then: schema => schema.min(1, 'Select at least one user').required('Users are required'),
    otherwise: schema => schema.nullable(),
  }),

  itemScope: yup
    .string()
    .oneOf(['one_time_only', 'subscription_only', 'both'], 'Invalid item scope')
    .default('both'),

  subscriptionCancellationScope: yup
    .string()
    .oneOf(['cancellation_allowed', 'no_cancellation', 'both'], 'Invalid subscription type scope')
    .default('both'),

  subscriptionApplicability: yup
    .string()
    .oneOf(['first_cycle', 'all_cycles', 'limited_cycles'], 'Invalid subscription applicability')
    .default('first_cycle'),

  applicableCycleCount: yup
    .number()
    .typeError('Must be a number')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when(['subscriptionApplicability', 'itemScope'], {
      is: (subscriptionApplicability, itemScope) =>
        subscriptionApplicability === 'limited_cycles' && itemScope !== 'one_time_only',
      then: schema =>
        schema.required('Number of cycles is required').integer('Must be a whole number').min(1, 'Minimum 1 cycle'),
      otherwise: schema => schema.nullable(),
    }),
});

const getPromoFields = (isEdit = false, currencySymbol = '₹') => [
  {
    name: 'discountType',
    label: 'Discount Type',
    type: 'switch',
    required: true,
    switchLabels: {true: 'Percentage (%)', false: `Flat Amount (${currencySymbol})`},
    tooltip: 'Choose percentage discount or fixed amount',
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'promoScope',
    label: 'User Scope',
    type: 'switch',
    required: true,
    switchLabels: {true: 'Global (All Users)', false: 'Customer Specific'},
    tooltip: 'Global = all users can use | Specific = only selected users',
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'isVisibleOnCart',
    label: 'Show on Cart',
    type: 'switch',
    required: false,
    switchLabels: {true: 'Visible', false: 'Hidden'},
    tooltip: 'Show this promo on the cart page for eligible users',
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'code',
    label: 'Promo Code',
    type: 'text',
    required: true,
    disabled: isEdit,
    placeholder: 'e.g., WELCOME50',
    tooltip: isEdit ? 'Promo code cannot be changed after creation' : '4-10 characters, letters & numbers only',
    gridSize: {xs: 12, sm: 6},
  },
  {
    name: 'description',
    label: 'Description',
    type: 'text',
    required: false,
    placeholder: 'e.g., Welcome bonus for new users',
    tooltip: 'Brief description of the promo (max 250 chars)',
    gridSize: {xs: 12, sm: 6},
  },
  {
    name: 'discountValue',
    label: 'Discount Value',
    type: 'number',
    required: true,
    placeholder: 'e.g., 20',
    tooltip: 'For percentage: 1-100 | For flat: 1-10000',
    dynamicLabel: true,
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'maximumDiscountAmount',
    label: `Max Discount Cap (${currencySymbol})`,
    type: 'number',
    required: false,
    placeholder: 'e.g., 2000',
    tooltip: 'Maximum discount amount (for percentage type only)',
    showWhen: {field: 'discountType', value: 'percentage'},
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'minimumCartAmount',
    label: `Minimum Cart Amount (${currencySymbol})`,
    type: 'number',
    required: false,
    placeholder: 'e.g., 500',
    tooltip: 'Cart must have at least this amount',
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'expirationDate',
    label: 'Expiration Date & Time',
    type: 'date',
    required: true,
    tooltip: 'Promo will expire after this date',
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'maximumNumberOfUsage',
    label: 'Total Usage Limit',
    type: 'number',
    required: true,
    placeholder: 'e.g., 100',
    tooltip: 'Maximum total times this promo can be used',
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'allowedUsagesPerUser',
    label: 'Per User Limit',
    type: 'number',
    required: true,
    placeholder: 'e.g., 1',
    tooltip: 'How many times one user can use this (1-5)',
    inputProps: {min: 1, max: 5},
    gridSize: {xs: 12, sm: 4},
  },
  {
    name: 'allowedUserIds',
    label: 'Select Eligible Users',
    type: 'select',
    required: false,
    tooltip: 'Select users who can use this promo (customer-specific only)',
    showWhen: {field: 'promoScope', value: 'customer-specific'},
    gridSize: {xs: 12},
  },
];

const generatePromoCode = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < Math.min(length, 10); i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const formatUserLabel = user => {
  return `${user.firstName || ''} ${user.lastName || ''} (${user.email || 'No email'})`.trim();
};

const PromoCodeForm = () => {
  const {user} = useSelector(state => state.auth);
  const navigate = useNavigate();
  const {id: promoId} = useParams();
  const {countries, getCountryById} = useCountry();

  const [users, setUsers] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedUsersForDisplay, setSelectedUsersForDisplay] = useState([]);
  const [pendingAllowedIds, setPendingAllowedIds] = useState(null);
  const [stats, setStats] = useState(null);

  const {
    control,
    handleSubmit,
    formState: {errors, isDirty},
    setValue,
    watch,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(promoSchema),
    defaultValues: {
      discountType: 'percentage',
      promoScope: 'global',
      countryScope: 'all',
      isVisibleOnCart: true,
      code: '',
      description: '',
      discountValue: '',
      maximumDiscountAmount: '',
      minimumCartAmount: '',
      expirationDate: moment().add(7, 'days'),
      maximumNumberOfUsage: 50,
      allowedUsagesPerUser: 1,
      allowedCountryId: '',
      allowedUserIds: [],
      itemScope: 'both',
      subscriptionCancellationScope: 'both',
      subscriptionApplicability: 'first_cycle',
      applicableCycleCount: '',
    },
    mode: 'onChange',
  });

  const watchDiscountType = watch('discountType');
  const watchPromoScope = watch('promoScope');
  const watchCountryScope = watch('countryScope');
  const watchAllowedCountryId = watch('allowedCountryId');
  const watchItemScope = watch('itemScope');
  const watchSubscriptionApplicability = watch('subscriptionApplicability');
  const showSubscriptionOptions = watchItemScope !== 'one_time_only';

  const selectedCountry = watchCountryScope === 'specific' && watchAllowedCountryId
    ? getCountryById(watchAllowedCountryId)
    : null;

  const currencySymbol = selectedCountry?.currencySymbol || '₹';
  const promoFields = getPromoFields(isEdit, currencySymbol);

  useEffect(() => {
    if (promoId) {
      setIsEdit(true);
      fetchPromoDetails();
    }
  }, [promoId]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (watchItemScope === 'one_time_only') {
      setValue('subscriptionCancellationScope', 'both');
      setValue('subscriptionApplicability', 'first_cycle');
      setValue('applicableCycleCount', '');
    }
  }, [watchItemScope, setValue]);

  useEffect(() => {
    if (users.length > 0 && pendingAllowedIds && pendingAllowedIds.length > 0) {
      const selectedUsers = users.filter(u => pendingAllowedIds.includes(u.id));
      setSelectedUsersForDisplay(selectedUsers);
      setValue('allowedUserIds', pendingAllowedIds);
      setPendingAllowedIds(null);
    }
  }, [users, pendingAllowedIds]);

  useEffect(() => {
    if (watchDiscountType === 'percentage') {
      trigger('maximumDiscountAmount');
    }
  }, [watchDiscountType, trigger]);

  const fetchUsers = async () => {
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/user/admin/users`, {
        headers: {Authorization: `Bearer ${user?.accessToken}`},
      });
      setUsers(data?.data?.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPromoDetails = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/promo-code/get`, {
        headers: {Authorization: `Bearer ${user?.accessToken}`},
        params: {promoCodeOrId: promoId},
      });

      const promo = data?.data;
      if (!promo) {
        setFetchError('Promo code not found');
        return;
      }

      const formValues = {
        code: promo.code,
        description: promo.description || '',
        discountValue: promo.discountValue,
        discountType: promo.discountType,
        maximumDiscountAmount: promo.maximumDiscountAmount || '',
        minimumCartAmount: promo.minimumCartAmount || 0,
        expirationDate: promo.expirationDate ? moment(promo.expirationDate) : moment().add(7, 'days'),
        maximumNumberOfUsage: promo.maximumNumberOfUsage || 50,
        allowedUsagesPerUser: promo.allowedUsagesPerUser || 1,
        promoScope: promo.promoScope || 'global',
        countryScope: promo.countryScope || 'all',
        allowedCountryId: promo.allowedCountryIds?.[0] || '',
        isVisibleOnCart: promo.isVisibleOnCart !== undefined ? promo.isVisibleOnCart : true,
        allowedUserIds: [],
        itemScope: promo.itemScope || 'both',
        subscriptionCancellationScope: promo.subscriptionCancellationScope || 'both',
        subscriptionApplicability: promo.subscriptionApplicability || 'first_cycle',
        applicableCycleCount: promo.applicableCycleCount || '',
      };

      try {
        const usageData = await fetchUsageStats(promo.id);
        if (usageData) setStats(usageData);
      } catch (err) {
        console.warn('Could not fetch usage stats:', err);
      }

      if (promo.promoScope === 'customer-specific') {
        try {
          const usageRecords = await fetchPromoUsageRecords(promo.id);
          const allowedIds = (usageRecords || []).map(r => r.userId);
          formValues.allowedUserIds = allowedIds;

          if (users.length > 0) {
            const selectedUsers = users.filter(u => allowedIds.includes(u.id));
            setSelectedUsersForDisplay(selectedUsers);
          } else {
            setPendingAllowedIds(allowedIds);
          }
        } catch (err) {
          console.warn('Could not fetch allowed users:', err);
        }
      }

      reset(formValues);
      setValue('discountType', promo.discountType);
      setValue('promoScope', promo.promoScope);
      setValue('countryScope', promo.countryScope || 'all');
      setValue('isVisibleOnCart', promo.isVisibleOnCart);
      setValue('allowedCountryId', promo.allowedCountryIds?.[0] || '');
    } catch (error) {
      console.error('Error fetching promo:', error);
      const message = error?.response?.data?.message || 'Failed to load promo code details';
      setFetchError(message);
      Notify.failure(message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsageStats = async promoId => {
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/promo-code/stats/${promoId}`, {
        headers: {Authorization: `Bearer ${user?.accessToken}`},
      });
      return data?.data || null;
    } catch {
      return null;
    }
  };

  const fetchPromoUsageRecords = async promoId => {
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/promo-code/usages/${promoId}`, {
        headers: {Authorization: `Bearer ${user?.accessToken}`},
      });
      return data?.data || [];
    } catch {
      return [];
    }
  };

  const handleGenerateCode = () => {
    setValue('code', generatePromoCode(), {shouldValidate: true, shouldDirty: true});
  };

  const onSubmit = async formData => {
    setIsSubmitting(true);

    const payload = {
      ...formData,
      expirationDate: moment(formData.expirationDate).toISOString(),
      discountType: formData.discountType,
      promoScope: formData.promoScope,
      countryScope: formData.countryScope,
    };

    if (payload.countryScope === 'specific' && payload.allowedCountryId) {
      payload.allowedCountryIds = [payload.allowedCountryId];
    }
    delete payload.allowedCountryId;

    if (payload.discountType !== 'percentage') {
      delete payload.maximumDiscountAmount;
    }
    if (payload.promoScope !== 'customer-specific') {
      delete payload.allowedUserIds;
    }
    if (payload.countryScope !== 'specific') {
      delete payload.allowedCountryIds;
    }
    if (payload.itemScope === 'one_time_only') {
      payload.subscriptionCancellationScope = 'both';
      payload.subscriptionApplicability = 'first_cycle';
      payload.applicableCycleCount = null;
    } else if (payload.subscriptionApplicability !== 'limited_cycles') {
      payload.applicableCycleCount = null;
    }

    try {
      if (isEdit) {
        payload.promoCodeOrId = promoId;
        Loading.circle('Updating Promo Code...');
        await axios.patch(`${ENDPOINT}api/v1/promo-code/update`, payload, {
          headers: {Authorization: `Bearer ${user?.accessToken}`},
        });
        Loading.remove();
        Notify.success('Promo code updated successfully!');
      } else {
        Loading.circle('Creating Promo Code...');
        await axios.post(`${ENDPOINT}api/v1/promo-code/create`, payload, {
          headers: {Authorization: `Bearer ${user?.accessToken}`},
        });
        Loading.remove();
        Notify.success('Promo code created successfully!');
      }
      setTimeout(() => navigate('/promocodes'), 1500);
    } catch (error) {
      Loading.remove();
      const message = error?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} promo code`;
      Notify.failure(message);
      console.error('Error saving promo:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      Confirm.show(
        'Discard Changes?',
        'You have unsaved changes. Are you sure you want to leave?',
        'Yes, Leave',
        'Stay',
        () => navigate('/promocodes'),
        () => {},
      );
    } else {
      navigate('/promocodes');
    }
  };

  const renderField = field => {
    const {
      name, label, type, required, disabled, placeholder,
      tooltip, showWhen, dynamicLabel, switchLabels, inputProps, gridSize,
    } = field;

    if (showWhen) {
      const dependentValue = watch(showWhen.field);
      if (dependentValue !== showWhen.value) return null;
    }

    let displayLabel = label;
    if (dynamicLabel && name === 'discountValue') {
      displayLabel = watchDiscountType === 'percentage'
        ? 'Discount Percentage (%)'
        : `Discount Amount (${currencySymbol})`;
    }

    return (
      <Grid item {...gridSize} key={name}>
        <Controller
          name={name}
          control={control}
          render={({field: controllerField, fieldState: {error}}) => {
            if (type === 'switch') {
              return (
                <Tooltip title={tooltip || ''} arrow placement="top">
                  <FormControl component="fieldset" error={!!error} fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            controllerField.value === true ||
                            controllerField.value === 'global' ||
                            controllerField.value === 'percentage'
                          }
                          onChange={e => {
                            let value;
                            if (name === 'promoScope') {
                              value = e.target.checked ? 'global' : 'customer-specific';
                            } else if (name === 'discountType') {
                              value = e.target.checked ? 'percentage' : 'flat';
                            } else {
                              value = e.target.checked;
                            }
                            controllerField.onChange(value);
                          }}
                          disabled={disabled}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {switchLabels
                              ? controllerField.value === true ||
                                controllerField.value === 'global' ||
                                controllerField.value === 'percentage'
                                ? switchLabels.true
                                : switchLabels.false
                              : label}
                          </Typography>
                          {required && <Typography variant="caption" color="error">*</Typography>}
                        </Box>
                      }
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </FormControl>
                </Tooltip>
              );
            }

            if (type === 'date') {
              return (
                <Tooltip title={tooltip || ''} arrow placement="top">
                  <Box>
                    <DateTimePickerForm field={controllerField} label={`${displayLabel}${required ? ' *' : ''}`} />
                    {error && <FormHelperText error sx={{mt: 0.5}}>{error.message}</FormHelperText>}
                  </Box>
                </Tooltip>
              );
            }

            if (type === 'select') {
              return (
                <Tooltip title={tooltip || ''} arrow placement="top">
                  <Box>
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={formatUserLabel}
                      value={selectedUsersForDisplay}
                      filterSelectedOptions
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, selectedOptions) => {
                        setSelectedUsersForDisplay(selectedOptions);
                        controllerField.onChange(selectedOptions.map(u => u.id));
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={`${displayLabel}${required ? ' *' : ''}`}
                          placeholder="Search users by name or email..."
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={formatUserLabel(option)} {...getTagProps({index})} size="small" key={option.id} />
                        ))
                      }
                      disabled={disabled}
                    />
                  </Box>
                </Tooltip>
              );
            }

            return (
              <Tooltip title={tooltip || ''} arrow placement="top">
                <MDInput
                  {...controllerField}
                  label={`${displayLabel}${required ? ' *' : ''}`}
                  fullWidth
                  type={type || 'text'}
                  placeholder={placeholder}
                  disabled={disabled}
                  error={!!error}
                  helperText={error?.message}
                  inputProps={inputProps}
                  InputProps={
                    name === 'code' && !isEdit
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Generate random code">
                                <IconButton onClick={handleGenerateCode} edge="end" size="small">
                                  <Icon>refresh</Icon>
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                  onChange={e => {
                    let value = e.target.value;
                    if (name === 'code') {
                      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    }
                    if (type === 'number') {
                      value = value === '' ? '' : Number(value);
                    }
                    controllerField.onChange(value);
                  }}
                />
              </Tooltip>
            );
          }}
        />
      </Grid>
    );
  };

  if (isEdit && isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} mb={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={48} />
            <MDTypography variant="h6" mt={2}>Loading promo details...</MDTypography>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (isEdit && fetchError) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} mb={3}>
          <Card sx={{padding: 4, textAlign: 'center'}}>
            <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
            <MDTypography variant="h5" mb={2}>{fetchError}</MDTypography>
            <MDButton variant="contained" onClick={() => navigate('/promocodes')}>Back to Promo Codes</MDButton>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Card sx={{margin: 'auto', padding: 3, width: '100%'}}>
              <MDBox textAlign="start" mb={3}>
                <MDTypography variant="h4" fontWeight="medium">
                  {isEdit ? 'Update Promo Code' : 'Create New Promo Code'}
                </MDTypography>
                {isEdit && (
                  <MDTypography variant="body2" color="textSecondary" mt={1}>
                    Editing: <strong>{watch('code')}</strong>
                  </MDTypography>
                )}
                {selectedCountry && (
                  <Chip
                    label={`${selectedCountry.flagEmoji || ''} ${selectedCountry.countryName} (${selectedCountry.currencySymbol})`}
                    color="primary"
                    size="small"
                    sx={{mt: 1}}
                  />
                )}
              </MDBox>

              {isEdit && stats && (
                <Alert severity="info" sx={{mb: 3}}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption">Total Used</Typography>
                      <Typography variant="h6">{stats.totalUsed || 0}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption">Remaining</Typography>
                      <Typography variant="h6">
                        {Math.max(0, (watch('maximumNumberOfUsage') || 0) - (stats.totalUsed || 0))}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption">Unique Users</Typography>
                      <Typography variant="h6">{stats.uniqueUsers || 0}</Typography>
                    </Grid>
                  </Grid>
                  {stats.totalUsed > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={(stats.totalUsed / watch('maximumNumberOfUsage')) * 100}
                      sx={{mt: 1, height: 8, borderRadius: 4}}
                    />
                  )}
                </Alert>
              )}

              <MDBox pt={2} pb={3}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Grid container spacing={2}>

                    {/* ROW 1: COUNTRY SCOPE + DROPDOWN */}
                    <Grid item xs={12}>
                      <MDBox display="flex" flexWrap="wrap" gap={3} alignItems="center" p={2} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200">
                        <Controller
                          name="countryScope"
                          control={control}
                          render={({field}) => (
                            <FormControl component="fieldset">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={field.value === 'all'}
                                    onChange={e => field.onChange(e.target.checked ? 'all' : 'specific')}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontWeight={500}>
                                    {field.value === 'all' ? 'All Countries' : 'Specific Country'}
                                  </Typography>
                                }
                              />
                            </FormControl>
                          )}
                        />
                        {watchCountryScope === 'specific' && (
                          <Box flex={1} minWidth={300}>
                            <Controller
                              name="allowedCountryId"
                              control={control}
                              render={({field: cField, fieldState: {error}}) => (
                                <CountryDropdown
                                  value={cField.value || ''}
                                  onChange={cField.onChange}
                                  label="Select Country"
                                  required={true}
                                  error={!!error}
                                  helperText={error?.message}
                                  showAllOption={false}
                                  autoSelectDefault={false}
                                />
                              )}
                            />
                          </Box>
                        )}
                      </MDBox>
                    </Grid>

                    {/* ROW 2: USER SCOPE + DROPDOWN */}
                    <Grid item xs={12}>
                      <MDBox display="flex" flexWrap="wrap" gap={3} alignItems="flex-start" p={2} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200">
                        {renderField(promoFields.find(f => f.name === 'promoScope'))}
                        {watchPromoScope === 'customer-specific' && (
                          <Box flex={1} minWidth={300}>
                            {renderField(promoFields.find(f => f.name === 'allowedUserIds'))}
                          </Box>
                        )}
                      </MDBox>
                    </Grid>

                    {/* ROW 3: DISCOUNT TYPE + SHOW ON CART */}
                    <Grid item xs={12}>
                      <MDBox display="flex" flexWrap="wrap" gap={3} alignItems="center" p={2} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200">
                        {renderField(promoFields.find(f => f.name === 'discountType'))}
                        {renderField(promoFields.find(f => f.name === 'isVisibleOnCart'))}
                      </MDBox>
                    </Grid>

                    {/* ROW 3.4: ITEM SCOPE */}
                    <Grid item xs={12}>
                      <MDBox p={2} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200">
                        <Tooltip
                          title="Restricts this promo to one-time items, subscription items, or both when they share a cart"
                          arrow
                          placement="top"
                        >
                          <FormLabel component="legend" sx={{mb: 1}}>
                            <Typography variant="body2" fontWeight={500} color="text.primary">
                              Applicable On
                            </Typography>
                          </FormLabel>
                        </Tooltip>
                        <Controller
                          name="itemScope"
                          control={control}
                          render={({field}) => (
                            <RadioGroup
                              row
                              value={field.value}
                              onChange={e => field.onChange(e.target.value)}
                            >
                              <FormControlLabel
                                value="one_time_only"
                                control={<Radio size="small" />}
                                label="One-time Items Only"
                              />
                              <FormControlLabel
                                value="subscription_only"
                                control={<Radio size="small" />}
                                label="Subscription Items Only"
                              />
                              <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
                            </RadioGroup>
                          )}
                        />
                      </MDBox>
                    </Grid>

                    {/* ROW 3.5: SUBSCRIPTION-ONLY OPTIONS (cancellation type + discount duration) */}
                    {showSubscriptionOptions && (
                      <Grid item xs={12}>
                        <MDBox p={2} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200" display="flex" flexDirection="column" gap={2}>
                          <MDBox>
                            <Tooltip
                              title="Restricts this promo to cancellable subscriptions, non-cancellable (locked-in) subscriptions, or both"
                              arrow
                              placement="top"
                            >
                              <FormLabel component="legend" sx={{mb: 1}}>
                                <Typography variant="body2" fontWeight={500} color="text.primary">
                                  Subscription Type
                                </Typography>
                              </FormLabel>
                            </Tooltip>
                            <Controller
                              name="subscriptionCancellationScope"
                              control={control}
                              render={({field}) => (
                                <RadioGroup
                                  row
                                  value={field.value}
                                  onChange={e => field.onChange(e.target.value)}
                                >
                                  <FormControlLabel
                                    value="cancellation_allowed"
                                    control={<Radio size="small" />}
                                    label="Cancellable Only"
                                  />
                                  <FormControlLabel
                                    value="no_cancellation"
                                    control={<Radio size="small" />}
                                    label="Non-cancellable Only"
                                  />
                                  <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
                                </RadioGroup>
                              )}
                            />
                          </MDBox>

                          <MDBox>
                            <Tooltip
                              title="Controls how this discount applies to subscription items across their billing cycles"
                              arrow
                              placement="top"
                            >
                              <FormLabel component="legend" sx={{mb: 1}}>
                                <Typography variant="body2" fontWeight={500} color="text.primary">
                                  Subscription Discount Duration
                                </Typography>
                              </FormLabel>
                            </Tooltip>
                            <MDBox display="flex" flexWrap="wrap" gap={3} alignItems="center">
                              <Controller
                                name="subscriptionApplicability"
                                control={control}
                                render={({field}) => (
                                  <RadioGroup
                                    row
                                    value={field.value}
                                    onChange={e => field.onChange(e.target.value)}
                                  >
                                    <FormControlLabel
                                      value="first_cycle"
                                      control={<Radio size="small" />}
                                      label="First billing cycle only"
                                    />
                                    <FormControlLabel
                                      value="all_cycles"
                                      control={<Radio size="small" />}
                                      label="Every billing cycle (full duration)"
                                    />
                                    <FormControlLabel
                                      value="limited_cycles"
                                      control={<Radio size="small" />}
                                      label="Limited number of cycles"
                                    />
                                  </RadioGroup>
                                )}
                              />
                              {watchSubscriptionApplicability === 'limited_cycles' && (
                                <Box minWidth={160}>
                                  <Controller
                                    name="applicableCycleCount"
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                      <MDInput
                                        {...field}
                                        label="Number of Cycles *"
                                        type="number"
                                        size="small"
                                        placeholder="e.g., 6"
                                        error={!!error}
                                        helperText={error?.message}
                                        inputProps={{min: 1}}
                                        onChange={e => {
                                          const value = e.target.value;
                                          field.onChange(value === '' ? '' : Number(value));
                                        }}
                                      />
                                    )}
                                  />
                                </Box>
                              )}
                            </MDBox>
                            <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
                              Only applies when this promo is used on a subscription item.
                            </Typography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    )}

                    {/* REMAINING INPUT FIELDS */}
                    {promoFields
                      .filter(f =>
                        f.type !== 'switch' &&
                        f.type !== 'select' &&
                        f.name !== 'countryScope' &&
                        f.name !== 'allowedCountryId'
                      )
                      .map(f => renderField(f))}

                  </Grid>

                  <MDBox mt={4} display="flex" gap={2} justifyContent="flex-end">
                    <MDButton variant="outlined" color="secondary" onClick={handleCancel} type="button">Cancel</MDButton>
                    <MDButton variant="gradient" color="info" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <><CircularProgress size={20} sx={{mr: 1, color: 'white'}} />{isEdit ? 'Updating...' : 'Creating...'}</>
                      ) : isEdit ? 'Update Promo Code' : 'Create Promo Code'}
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default PromoCodeForm;
